import { makeAutoObservable, runInAction } from 'mobx';
import {
  attachmentApi,
  type CombinedAttachment,
} from '../api/attachment_api';
import { fileApi } from '../api/file_api';
import { fileAssociationApi } from '../api/file_association_api';

export class AttachmentStore {
  // Attachments indexed by key
  attachments: Map<string, CombinedAttachment> = new Map();
  // Attachments indexed by request key
  attachmentsByRequest: Map<string, string[]> = new Map();
  // Signed URLs cache (attachment key -> url)
  signedUrls: Map<string, string> = new Map();

  // Loading state
  loading = false;
  uploading = false;

  // Error state
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Computed getters
  getAttachmentsByRequest(requestKey: string): CombinedAttachment[] {
    const keys = this.attachmentsByRequest.get(requestKey) || [];
    return keys
      .map((k) => this.attachments.get(k))
      .filter((a): a is CombinedAttachment => a !== undefined)
      .sort((a, b) => {
        const dateA = a.date_created ? new Date(a.date_created).getTime() : 0;
        const dateB = b.date_created ? new Date(b.date_created).getTime() : 0;
        return dateB - dateA;
      });
  }

  getAttachmentByKey(key: string): CombinedAttachment | undefined {
    return this.attachments.get(key);
  }

  getSignedUrl(attachmentKey: string): string | undefined {
    return this.signedUrls.get(attachmentKey);
  }

  // Actions
  clearError(): void {
    this.error = null;
  }

  /**
   * Fetch attachments for a request (both legacy and new File+FileAssociation)
   */
  async fetchByRequest(requestKey: string): Promise<void> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      const response = await attachmentApi.listCombined(requestKey);

      runInAction(() => {
        const allAttachmentKeys: string[] = [];
        response.items.forEach((attachment) => {
          this.attachments.set(attachment.key, attachment);
          allAttachmentKeys.push(attachment.key);
        });
        this.attachmentsByRequest.set(requestKey, allAttachmentKeys);
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.loading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch attachments';
      });
    }
  }

  /**
   * Upload a file and associate it with a request
   */
  async uploadAttachment(
    requestKey: string,
    requestUuid: string,
    file: File
  ): Promise<CombinedAttachment | undefined> {
    runInAction(() => {
      this.uploading = true;
      this.error = null;
    });

    try {
      // 1. Upload file using existing file API
      const fileRecord = await fileApi.uploadFile(file, 'attachment');

      // 2. Create association with request
      const association = await fileAssociationApi.create(
        fileRecord.uuid,
        'request',
        requestUuid
      );

      // 3. Create combined attachment for local store
      const newAttachment: CombinedAttachment = {
        type: 'file',
        key: association.uuid,
        uuid: association.uuid,
        file_uuid: fileRecord.uuid,
        filename: fileRecord.filename,
        content_type: fileRecord.content_type,
        filesize: null,
        date_created: association.date_created,
      };

      // 4. Add to local store
      runInAction(() => {
        this.attachments.set(newAttachment.key, newAttachment);
        const existing = this.attachmentsByRequest.get(requestKey) || [];
        this.attachmentsByRequest.set(requestKey, [
          newAttachment.key,
          ...existing,
        ]);
        this.uploading = false;
      });

      return newAttachment;
    } catch (err) {
      runInAction(() => {
        this.uploading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to upload attachment';
      });
      return undefined;
    }
  }

  /**
   * Get a signed download URL for an attachment
   */
  async fetchSignedUrl(
    attachment: CombinedAttachment,
    disposition: 'inline' | 'attachment' = 'attachment'
  ): Promise<string | undefined> {
    // Check cache first
    const cached = this.signedUrls.get(attachment.key);
    if (cached) return cached;

    try {
      let url: string;

      if (attachment.type === 'attachment') {
        // Legacy FHRequestAttachment - use old endpoint
        const result = await attachmentApi.getSignedUrl(
          attachment.key,
          disposition
        );
        url = result['signed-url'];
      } else {
        // New File+FileAssociation - use file endpoint
        const result = await fileApi.getDownloadUrl(
          attachment.key, // This is the association UUID
          disposition
        );
        url = result.url;
      }

      runInAction(() => {
        this.signedUrls.set(attachment.key, url);
      });

      return url;
    } catch (err) {
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to get download URL';
      });
      return undefined;
    }
  }

  /**
   * Download an attachment (opens in new tab or triggers download)
   */
  async downloadAttachment(
    attachmentKey: string,
    disposition: 'inline' | 'attachment' = 'attachment'
  ): Promise<void> {
    const attachment = this.attachments.get(attachmentKey);
    if (!attachment) {
      this.error = 'Attachment not found';
      return;
    }

    const url = await this.fetchSignedUrl(attachment, disposition);
    if (url) {
      window.open(url, '_blank');
    }
  }

  /**
   * Clear signed URLs cache (they expire after some time)
   */
  clearSignedUrls(): void {
    runInAction(() => {
      this.signedUrls.clear();
    });
  }

  /**
   * Receive realtime update for an attachment
   */
  receiveCombinedAttachmentUpdate(
    requestKey: string,
    attachment: CombinedAttachment
  ): void {
    runInAction(() => {
      this.attachments.set(attachment.key, attachment);

      const existing = this.attachmentsByRequest.get(requestKey) || [];
      if (!existing.includes(attachment.key)) {
        this.attachmentsByRequest.set(requestKey, [attachment.key, ...existing]);
      }
    });
  }
}

export const attachmentStore = new AttachmentStore();
